import {create} from 'zustand';
import {$api} from 'shared/api';
import {HivesRow} from 'features/hives/ui/hivesTable/types';
import {
  GetHivesParams,
  HiveCoordinates,
  HivesResponse,
  HivesState,
  SharedHive,
} from 'entities/hives/model/types.ts';
import {serializeParams} from 'shared/helpers/converters';
import {toast} from 'react-toastify';

const SHARE_HOST = '';

const toNum = (v: any) => (v === '' || v == null ? undefined : Number(v));

export const convertApiHiveToInternal = (api: any): HivesRow => {
  return {
    id: api.id,
    number: api.number ?? 0,
    apiary: api.apiary,
    uterus: {
      color: api.uterus?.color ?? '',
      year: api.uterus?.year ?? null,
      mark: api.uterus?.mark ?? null,
      breed: api.uterus?.breed ?? null,
    },
    states: api.dictionaries.map((el: any) => el.name),
    frames: api.frames ?? 0,
    strength: api.strength ?? 0,
    brood: api.brood ?? 0,
    devices: api.devices ?? 0,
    happiness: api.happiness ?? 0,
    isSmart: api.devices > 0,
    createdAt: api.createdAt ?? '',
    updatedAt: api.updatedAt ?? '',
    deletedAt: api.deletedAt ?? null,
  };
};

export const useHivesStore = create<HivesState>((set, get) => ({
  hives: [],
  isLoading: false,
  isOpenPop: false,
  currentPage: 0,
  pageSize: 200,
  isEnd: false,
  totalCount: null,
  lastParams: null,
  sharedHives: [],
  sharePath: SHARE_HOST + '/bee-share/hives',
  isLocationModalOpen: false,

  getHivesFromServer: async (params: GetHivesParams, replace = true): Promise<HivesResponse> => {
    set({isLoading: true});

    const paramsClone: GetHivesParams =
      structuredClone?.(params) || JSON.parse(JSON.stringify(params));
    Object.keys(paramsClone).forEach((k) => {
      const key = k as keyof GetHivesParams;
      const v = paramsClone[key] as any;
      if (v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) {
        delete paramsClone[key];
      }
    });

    if (!paramsClone.page) {
      paramsClone.page = {index: 0, size: get().pageSize};
      set({currentPage: 0, isEnd: false});
    }
    set({lastParams: paramsClone});

    let res: HivesResponse | null = null;

    try {
      const sortBy = (paramsClone as any).sortBy ?? 'updatedAt';
      const sort = (paramsClone as any).sort ?? 'DESC';

      const query: Record<string, any> = {
        apiaryId: paramsClone.apiaryId,
        hiveNumber: (paramsClone as any).numberQuery || undefined,
        hiveIds: paramsClone.hiveIds?.length ? paramsClone.hiveIds : undefined,
        framesFrom: toNum((paramsClone as any).framesFrom),
        framesTo: toNum((paramsClone as any).framesTo),
        broodFrom: toNum((paramsClone as any).broodFrom),
        broodTo: toNum((paramsClone as any).broodTo),
        strengthFrom: toNum((paramsClone as any).powerFrom),
        strengthTo: toNum((paramsClone as any).powerTo),
        uterusYear: (paramsClone as any).year ?? undefined,
        uterusMark: (paramsClone as any).markNumber ?? undefined,
        uterusBreed: (paramsClone as any).breed ?? undefined,
        dictionariesIds: (paramsClone as any).dictionariesIds?.length
          ? (paramsClone as any).dictionariesIds
          : undefined,
        sortBy,
        sort,
        page: paramsClone.page,
      };

      const http = await $api.get<any>('/hives', {
        params: query,
        paramsSerializer: {serialize: serializeParams},
      });

      const itemsApi = http?.data?.items ?? [];
      const pagination = http?.data?.pagination ?? {};
      const totalTop =
        typeof http?.data?.total === 'number' ? (http.data.total as number) : undefined;

      const items: HivesRow[] = itemsApi.map(convertApiHiveToInternal);

      res = {
        items,
        page: {
          index:
            typeof pagination.index === 'number'
              ? pagination.index
              : (paramsClone.page?.index ?? 0),
          size:
            typeof pagination.size === 'number'
              ? pagination.size
              : (paramsClone.page?.size ?? get().pageSize),
          total: totalTop,
        },
      };
    } catch {
      console.warn('[HivesStore] API недоступен');
    }

    if (!res) {
      set({isLoading: false});
      return {items: []};
    }

    const items = (res.items ?? []) as HivesRow[];
    const {hives, pageSize} = get();

    const prevPage = get().currentPage ?? 0;
    const size = res.page?.size ?? pageSize;
    const total = typeof res.page?.total === 'number' ? res.page.total : null;

    if (replace) {
      const idx = res.page?.index ?? 0;
      const reachedEnd = total != null ? (idx + 1) * size >= total : items.length < size;
      set({
        hives: items,
        currentPage: idx,
        pageSize: size,
        isEnd: reachedEnd,
        totalCount: total,
      });
    } else {
      const nextPage = prevPage + 1;
      const reachedEnd = total != null ? (nextPage + 1) * size >= total : items.length < size;
      set({
        hives: [...hives, ...items],
        currentPage: nextPage,
        pageSize: size,
        isEnd: reachedEnd,
        totalCount: total ?? get().totalCount,
      });
    }

    set({isLoading: false});
    return res;
  },

  getHiveLocation: async (hiveId: string) => {
    try {
      const {data} = await $api.get<HiveCoordinates[]>(`/collector/devices/coordinates`, {
        params: {
          hiveId,
        },
      });
      return data?.[data.length - 1] ?? null;
    } catch (err) {
      console.error('[HiveStore] API недоступен', err);
      return null;
    }
  },

  getNextPage: async (base: {apiaryId: string | number} | null) => {
    const {lastParams, currentPage, pageSize, isEnd, hives, totalCount} = get();
    if (isEnd) return;
    if (typeof totalCount === 'number' && hives.length >= totalCount) {
      set({isEnd: true});
      return;
    }
    if (!lastParams && !base) return;

    const paramsClone: GetHivesParams = structuredClone?.(lastParams) ||
      JSON.parse(JSON.stringify(lastParams)) || {apiaryId: base!.apiaryId};

    paramsClone.page = {index: currentPage + 1, size: pageSize};
    await get().getHivesFromServer(paramsClone, false);
  },

  getHives: async (apiaryId) => {
    try {
      const http = await $api.get<any>('/hives', {
        params: {
          apiaryId,
        },
        paramsSerializer: {serialize: serializeParams},
      });
      const itemsApi = http?.data?.items ?? [];
      const items: HivesRow[] = itemsApi.map(convertApiHiveToInternal);
      return items;
    } catch {
      return [];
    }
  },

  addHive: async (params) => {
    const res = await $api.post('/hives', params);
    return res;
  },

  managePopup: (status) => {
    set({isOpenPop: status});
  },

  reload: async () => {
    const {lastParams, pageSize, getHivesFromServer} = get();
    if (!lastParams) return;
    const params = {...lastParams, page: {index: 0, size: pageSize}};
    await getHivesFromServer(params, true);
  },

  getHivesByNumber: async (apiaryId, number) => {
    try {
      const http = await $api.get<any>('/hives', {
        params: {
          apiaryId,
          hiveNumber: String(number),
          page: {index: 0, size: 50},
        },
        paramsSerializer: {serialize: serializeParams},
      });
      const itemsApi = http?.data?.items ?? [];
      const items: HivesRow[] = itemsApi.map(convertApiHiveToInternal);
      return items;
    } catch {
      return [];
    }
  },

  getSharedHives: async () => {
    const {sharePath} = get();

    const res = await $api.get<SharedHive[]>(sharePath).catch(console.warn);
    const sharedHives = res?.data || [];

    set({
      sharedHives,
    });
  },

  shareHive: async (params) => {
    const {sharePath, getSharedHives} = get();

    let answer = true;
    await $api.post(sharePath, params).catch((err) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      err?.response?.data?.message && toast.error(err.response.data.message);
      answer = false;
    });

    if (answer) {
      getSharedHives();
    }

    return answer;
  },

  deShareHive: async (id) => {
    const {sharePath, getSharedHives} = get();

    let answer = true;
    await $api.delete(sharePath + `/${id}`).catch((err) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      err?.response?.data?.message && toast.error(err.response.data.message);
      answer = false;
    });

    if (answer) {
      getSharedHives();
    }

    return answer;
  },
  setLocationModalOpen: (open: boolean) => {
    return set({isLocationModalOpen: open});
  },
}));
